import { create } from "zustand";
import { api } from "@/lib/api";
import type { UsageEvent, UsageSummary, DateRange } from "@/types/usage";

interface UsageState {
  events: UsageEvent[];
  summary: UsageSummary | null;
  dateRange: DateRange;
  isLoading: boolean;
  error: string | null;
}

interface UsageActions {
  loadUsage(dateRange?: DateRange): Promise<void>;
  loadSummary(): Promise<void>;
  exportCsv(): Promise<Blob | null>;
  setDateRange(dateRange: DateRange): void;
}

const defaultDateRange: DateRange = {
  start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0],
  end: new Date().toISOString().split("T")[0],
};

export const useUsageStore = create<UsageState & UsageActions>((set, get) => ({
  events: [],
  summary: null,
  dateRange: defaultDateRange,
  isLoading: false,
  error: null,

  async loadUsage(dateRange?: DateRange) {
    const range = dateRange ?? get().dateRange;
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({
        start: range.start,
        end: range.end,
      });
      const events = await api.get<UsageEvent[]>(
        `/api/dashboard/usage?${params}`,
      );
      set({ events, dateRange: range, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error:
          err instanceof Error ? err.message : "Failed to load usage data",
      });
    }
  },

  async loadSummary() {
    set({ isLoading: true, error: null });
    try {
      const summary = await api.get<UsageSummary>(
        "/api/dashboard/usage/summary",
      );
      set({ summary, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error:
          err instanceof Error
            ? err.message
            : "Failed to load usage summary",
      });
    }
  },

  async exportCsv() {
    try {
      const range = get().dateRange;
      const params = new URLSearchParams({
        start: range.start,
        end: range.end,
      });
      const res = await fetch(
        `https://api.ajnt.ai/api/dashboard/usage/export?${params}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
      return await res.blob();
    } catch (err) {
      set({
        error:
          err instanceof Error ? err.message : "Failed to export CSV",
      });
      return null;
    }
  },

  setDateRange(dateRange: DateRange) {
    set({ dateRange });
  },
}));
